import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
  InfoOutlined as InfoOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Route as RouteIcon,
  ReceiptLong as ReceiptLongIcon,
  Summarize as SummarizeIcon,
  DirectionsBus as DirectionsBusIcon,
} from '@mui/icons-material';

import { useValuations } from '../../../hooks/administration/useValuations';
import formatDate from './utils/formatDate';
import OrderStatusChip from '../../utils/components/OrderStatusChip'; 

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const TRAMO_CONFIG = {
  IDA: { label: 'SUBIDA', color: '#2e7d32' },
  VUELTA: { label: 'BAJADA', color: '#1565c0' },
};

const MANIFEST_STATUS_CONFIG = {
  CREADO: { label: 'CREADO', color: '#90a4ae', text: '#fff' },
  PLANIFICADO: { label: 'PLANIFICADO', color: '#ffb300', text: '#000' },
  EN_PROCESO: { label: 'EN PROCESO', color: '#4caf50', text: '#fff' },
  COMPLETADO: { label: 'COMPLETADO', color: '#1e88e5', text: '#fff' },
  CANCELADO: { label: 'CANCELADO', color: '#e53935', text: '#fff' },
};

const normalizeText = (value) => String(value || '').trim().toUpperCase();

const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary.main',
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.8,
      height: '100%',
      minWidth: 0,
      borderRadius: 3,
      background:
        'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
      transition: 'all 0.2s ease-in-out',
      overflow: 'hidden',
      '&:hover': {
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center" minWidth={0}>
      <Box
        sx={{
          width: 34,
          height: 34,
          minWidth: 34,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        }}
      >
        {icon}
      </Box>

      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {title}
        </Typography>

        <Typography
          variant="subtitle1"
          fontWeight="bold"
          lineHeight={1.25}
          noWrap
          title={String(value)}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={String(subtitle)}
            display="block"
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const EligibleOrdersDialog = ({
  open,
  handleClose,
  valuation,
  detail = null,
  onConfirm,
  loading = false,
}) => {
  const { useEligibleOrders } = useValuations();

  const isEditMode = Boolean(detail?.valdetid);
  const symbol = valuation?.monsim || 'S/';

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [serProID, setSerProID] = useState('');
  const [valDetDes, setValDetDes] = useState('');
  const [valDetBasCal, setValDetBasCal] = useState('OS');
  const [expandedOrders, setExpandedOrders] = useState({});  

  useEffect(() => {
    if (!open) return;

    setSearch('');
    setPage(0);
    setRowsPerPage(10);
    setExpandedOrders({});

    if (isEditMode) {
      setSerProID(detail?.serproid || '');
      setValDetDes(detail?.valdetdes || '');
      setValDetBasCal(detail?.valdetbascal || 'OS');
      setSelectedIds(
        Array.isArray(detail?.orders)
          ? detail.orders.map((order) => order.ordsercabid)
          : []
      );
      return;
    }

    setSerProID('');
    setValDetDes('');
    setValDetBasCal('OS');
    setSelectedIds([]);
  }, [open, isEditMode, detail]);

  const {
    data: eligibleOrders = [],
    isLoading,
    isFetching,
    error,
  } = useEligibleOrders({
    proID: valuation?.proid,
    fecIni: valuation?.valcabfecini,
    fecFin: valuation?.valcabfecfin,
    search,
    limit: 1000,
    offset: 0,
    enabled: open,
  });

  const getManifests = (order) =>
    Array.isArray(order?.manifiestos) ? order.manifiestos : [];

  const getCompletedManifests = (order) =>
    getManifests(order).filter(
      (mn) => normalizeText(mn.manest) === 'COMPLETADO'
    );

  const getValorizableManifests = (order) => {
    const orderState = normalizeText(order?.ordsercabest);
    const manifests = getManifests(order);

    if (['EN_EJECUCION', 'PRE_VALORIZADA'].includes(orderState)) {
      return manifests.filter(
        (mn) => normalizeText(mn.manest) !== 'CANCELADO'
      );
    }

    return manifests.filter(
      (mn) => normalizeText(mn.manest) === 'COMPLETADO'
    );
  };

  const getCompleteTrips = (order) => {
    const completed = getValorizableManifests(order);

    const idas = completed.filter(
      (mn) => normalizeText(mn.ordsermantra) === 'IDA'
    ).length;

    const vueltas = completed.filter(
      (mn) => normalizeText(mn.ordsermantra) === 'VUELTA'
    ).length;

    return Math.min(idas, vueltas);
  };

  const serviceTypeOptions = useMemo(() => {
    const map = new Map();

    eligibleOrders.forEach((order) => {
      if (!map.has(order.serproid)) {
        map.set(order.serproid, {
          serProID: order.serproid,
          serProNom: order.serpronom,
          serProUni: order.serprouni,
        });
      }
    });

    if (isEditMode && detail?.serproid && !map.has(detail.serproid)) {
      map.set(detail.serproid, {
        serProID: detail.serproid,
        serProNom: detail.serpronom,
        serProUni: detail.serprouni,
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      String(a.serProNom || '').localeCompare(String(b.serProNom || ''))
    );
  }, [eligibleOrders, isEditMode, detail]);

  const currentDetailOrders = useMemo(() => {
    if (!isEditMode || !Array.isArray(detail?.orders)) return [];
    return detail.orders;
  }, [isEditMode, detail]);

  const mergedOrders = useMemo(() => {
    const selectedSerProID = Number(serProID || 0);
    if (!selectedSerProID) return [];

    const map = new Map();

    currentDetailOrders.forEach((order) => {
      if (Number(order.serproid) === selectedSerProID) {
        map.set(order.ordsercabid, order);
      }
    });

    eligibleOrders.forEach((order) => {
      if (Number(order.serproid) === selectedSerProID) {
        map.set(order.ordsercabid, order);
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      const dateA = a?.ordsercabfec ? String(a.ordsercabfec) : '';
      const dateB = b?.ordsercabfec ? String(b.ordsercabfec) : '';

      if (dateA !== dateB) return dateA.localeCompare(dateB);

      return Number(a?.ordsercabid || 0) - Number(b?.ordsercabid || 0);
    });
  }, [eligibleOrders, currentDetailOrders, serProID]);

  const paginatedOrders = useMemo(() => {
    const start = page * rowsPerPage;
    return mergedOrders.slice(start, start + rowsPerPage);
  }, [mergedOrders, page, rowsPerPage]);

  const selectedOrders = useMemo(
    () => mergedOrders.filter((order) => selectedIds.includes(order.ordsercabid)),
    [mergedOrders, selectedIds]
  );

  const selectedCompleteTripsCount = useMemo(() => {
    return selectedOrders.reduce(
      (sum, order) => sum + getCompleteTrips(order),
      0
    );
  }, [selectedOrders]);

  const totalSelectedAmount = useMemo(() => {
    return selectedOrders.reduce(
      (sum, order) => sum + Number(order.ordsercabpre || 0),
      0
    );
  }, [selectedOrders]);

  const selectedAverage = useMemo(() => {
    if (!selectedIds.length) return 0;
    return totalSelectedAmount / selectedIds.length;
  }, [totalSelectedAmount, selectedIds]);

  const selectedManifestCount = useMemo(() => {
    return selectedOrders.reduce(
      (sum, order) => sum + getValorizableManifests(order).length,
      0
    );
  }, [selectedOrders]);

  const visibleValorizableManifests = useMemo(() => {
    return mergedOrders.reduce(
      (sum, order) => sum + getValorizableManifests(order).length,
      0
    );
  }, [mergedOrders]);

  const selectedManifestAmount = useMemo(() => {
    return selectedOrders.reduce((sum, order) => {
      return (
        sum +
        getValorizableManifests(order).reduce(
          (subtotal, mn) => subtotal + Number(mn.ordsermanpre || 0),
          0
        )
      );
    }, 0);
  }, [selectedOrders]);

  const allVisibleSelected =
    paginatedOrders.length > 0 &&
    paginatedOrders.every((order) => selectedIds.includes(order.ordsercabid));

  const someVisibleSelected =
    paginatedOrders.some((order) => selectedIds.includes(order.ordsercabid)) &&
    !allVisibleSelected;

  const selectedServiceType = useMemo(() => {
    return serviceTypeOptions.find(
      (item) => Number(item.serProID) === Number(serProID)
    );
  }, [serviceTypeOptions, serProID]);

  const handleToggleAllVisible = () => {
    const visibleIds = paginatedOrders.map((order) => order.ordsercabid);

    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handleToggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleReset = () => {
    setSearch('');
    setPage(0);
    setExpandedOrders({});

    if (isEditMode) {
      setValDetDes(detail?.valdetdes || '');
      setValDetBasCal(detail?.valdetbascal || 'OS');
      setSelectedIds(
        Array.isArray(detail?.orders)
          ? detail.orders.map((order) => order.ordsercabid)
          : []
      );
      return;
    }

    setValDetDes('');
    setValDetBasCal('OS');
    setSelectedIds([]);
    setSerProID('');
  };

  const handleConfirm = () => {
    if (!serProID) return;

    onConfirm({
      serProID: Number(serProID),
      valDetDes: valDetDes?.trim() || null,
      valDetBasCal,
      orderIds: selectedIds,
    });
  };

  const confirmDisabled = !serProID || loading;

  /*
  const baseQuantity = valDetBasCal === 'MANIFIESTO'
    ? selectedManifestCount
    : selectedIds.length;

  const commercialUnitPrice = baseQuantity > 0
    ? totalSelectedAmount / baseQuantity
    : 0;

  const baseLabel = valDetBasCal === 'MANIFIESTO'
    ? 'Base manifiestos'
    : 'Base OS';
  */

  const baseQuantity = useMemo(() => {
    if (valDetBasCal === 'MANIFIESTO') return selectedManifestCount;
    if (valDetBasCal === 'VIAJE_COMPLETO') return selectedCompleteTripsCount;
    return selectedIds.length;
  }, [valDetBasCal, selectedManifestCount, selectedCompleteTripsCount, selectedIds.length]);

  const commercialUnitPrice = baseQuantity > 0
    ? totalSelectedAmount / baseQuantity
    : 0;

  const baseLabel =
    valDetBasCal === 'MANIFIESTO'
      ? 'Base manifiestos'
      : valDetBasCal === 'VIAJE_COMPLETO'
        ? 'Base viaje completo'
        : 'Base OS';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          height: { xs: '96vh', md: '92vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          background:
            'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #2563EB 100%)',
          color: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <ReceiptLongIcon />
              <Typography variant="h6" fontWeight="bold">
                {isEditMode
                  ? 'Editar ítem de valorización'
                  : 'Crear ítem de valorización'}
              </Typography>
            </Stack>

            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {isEditMode
                ? 'Actualiza la descripción, base de reporte y órdenes asociadas al ítem.'
                : 'Selecciona el servicio, define el criterio de reporte y asocia las órdenes correspondientes.'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`VAL-${String(valuation?.valcabid || 0).padStart(6, '0')}`}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.45)' }}
              variant="outlined"
            />
            <Chip
              label={valuation?.pronom || 'Proyecto'}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.45)' }}
              variant="outlined"
            />
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: '#F8FAFC',
          px: { xs: 2, md: 3 },
          py: 2.5,
        }}
      >
        <Stack spacing={2.5}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{ borderRadius: 3 }}
          >
            <Typography variant="body2">
              Período de valorización:{' '}
              <strong>{formatDate(valuation?.valcabfecini)}</strong> al{' '}
              <strong>{formatDate(valuation?.valcabfecfin)}</strong>. La base de
              reporte define cómo se mostrarán la cantidad y el P.U. en el PDF.
            </Typography>
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 3,
              backgroundColor: '#fff',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Servicio del proyecto"
                  value={serProID}
                  onChange={(e) => {
                    if (isEditMode) return;
                    setSerProID(e.target.value);
                    setSelectedIds([]);
                    setExpandedOrders({});
                    setPage(0);
                  }}
                  disabled={isEditMode || loading}
                  helperText={
                    selectedServiceType?.serProUni
                      ? `Unidad: ${selectedServiceType.serProUni}`
                      : 'Selecciona el servicio a valorizar.'
                  }
                >
                  {serviceTypeOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No hay servicios disponibles
                    </MenuItem>
                  ) : (
                    serviceTypeOptions.map((item) => (
                      <MenuItem key={item.serProID} value={item.serProID}>
                        {item.serProNom}
                        {item.serProUni ? ` (${item.serProUni})` : ''}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Base para reporte"
                  value={valDetBasCal}
                  onChange={(e) => setValDetBasCal(e.target.value)}
                  disabled={loading}
                  helperText={
                    valDetBasCal === 'MANIFIESTO'
                      ? 'El PDF usará manifiestos completados como cantidad.'
                      : valDetBasCal === 'VIAJE_COMPLETO'
                        ? 'El PDF usará pares completos IDA/VUELTA como cantidad.'
                        : 'El PDF usará órdenes de servicio como cantidad.'
                  }
                >
                  <MenuItem value="OS">Orden de servicio</MenuItem>
                  <MenuItem value="MANIFIESTO">Manifiesto completado</MenuItem>
                  <MenuItem value="VIAJE_COMPLETO">Viaje completo IDA/VUELTA</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar orden"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  placeholder="OS, nombre o servicio"
                  disabled={!serProID || loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Filtra las órdenes disponibles o asociadas."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={9}
                  size="small"
                  label="Descripción del ítem para el reporte"
                  value={valDetDes}
                  onChange={(e) => setValDetDes(e.target.value)}
                  placeholder={`Ejemplo:
SERVICIO DE TRASLADO DE PERSONAL MINIVAN - CAMBIO DE GUARDIA // QUELLAVECO
P.U. S/ 1200.00
FECHA SUBIDA Y BAJADA: 22 y 29 DE ABRIL & 06 y 12 DE MAYO
TOTAL 08 SERVICIOS`}
                  helperText="Puedes usar saltos de línea. Esta descripción aparecerá en el PDF."
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={valDetBasCal === 'MANIFIESTO' ? 'secondary' : 'primary'}
                      label={
                        valDetBasCal === 'MANIFIESTO'
                          ? 'Reporte por manifiestos'
                          : valDetBasCal === 'VIAJE_COMPLETO'
                            ? 'Reporte por viaje completo'
                            : 'Reporte por OS'
                      }
                    />
                    {isEditMode && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Editando detalle #${detail?.valdetid}`}
                      />
                    )}
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleReset}
                    disabled={loading}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Reiniciar selección
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
            }}
          >
            <MetricCard
              title="OS visibles"
              value={mergedOrders.length}
              subtitle={`${visibleValorizableManifests} MN valorizables`}
              icon={<DirectionsBusIcon />}
              color="primary.main"
            />

            <MetricCard
              title="OS seleccionadas"
              value={selectedIds.length}
              subtitle="Órdenes asociadas al ítem"
              icon={<SummarizeIcon />}
              color="info.main"
            />

            <MetricCard
              title="MN seleccionados"
              value={selectedManifestCount}
              subtitle={`${formatCurrency(selectedManifestAmount, symbol)} valorizables`}
              icon={<RouteIcon />}
              color="success.main"
            />

            <MetricCard
              title="Viajes completos"
              value={selectedCompleteTripsCount}
              subtitle="Pares IDA/VUELTA completados"
              icon={<RouteIcon />}
              color="secondary.main"
            />

            <MetricCard
              title="P.U. comercial"
              value={formatCurrency(commercialUnitPrice, symbol)}
              subtitle={baseLabel}
              icon={<ReceiptLongIcon />}
              color="secondary.main"
            />

            <MetricCard
              title="Total seleccionado"
              value={formatCurrency(totalSelectedAmount, symbol)}
              subtitle={baseLabel}
              icon={<ReceiptLongIcon />}
              color="warning.main"
            />
          </Box>



          {!serProID ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 3 }}>
              Selecciona primero un servicio del proyecto para listar las órdenes.
            </Alert>
          ) : isLoading ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                py: 7,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Stack spacing={1.5} alignItems="center">
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Cargando órdenes elegibles...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              {error?.message || 'Error al cargar órdenes elegibles.'}
            </Alert>
          ) : mergedOrders.length === 0 ? (
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 3 }}>
              No hay órdenes disponibles para el servicio seleccionado.
            </Alert>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography fontWeight="bold">
                      Órdenes de servicio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Despliega una OS para revisar sus manifiestos asociados.
                    </Typography>
                  </Box>

                  {isFetching && !isLoading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Actualizando...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              <TableContainer sx={{ maxHeight: 430 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={handleToggleAllVisible}
                        />
                      </TableCell>
                      <TableCell><strong>Código</strong></TableCell>
                      <TableCell><strong>Orden de servicio</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Fecha</strong></TableCell>
                      <TableCell align="center"><strong>MN</strong></TableCell>
                      <TableCell align="right"><strong>Precio OS</strong></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedOrders.map((order) => {
                      const checked = selectedIds.includes(order.ordsercabid);
                      const manifests = getManifests(order);
                      const valorizableManifests = getValorizableManifests(order);
                      const isExpanded = !!expandedOrders[order.ordsercabid];

                      return (
                        <React.Fragment key={order.ordsercabid}>
                          <TableRow
                            hover
                            selected={checked}
                            sx={{
                              cursor: 'pointer',
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              },
                            }}
                            onClick={() => handleToggleOne(order.ordsercabid)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} />
                            </TableCell>

                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title={isExpanded ? 'Ocultar manifiestos' : 'Ver manifiestos'}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleExpand(order.ordsercabid);
                                    }}
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>

                                <Typography fontWeight="bold" color="primary">
                                  OS-{String(order.ordsercabid).padStart(6, '0')}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ minWidth: 260, maxWidth: 360 }}>
                              <Typography noWrap fontWeight={500} title={order.ordsercabnom}>
                                {order.ordsercabnom}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {order.serpronom || '-'}s ASC
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <OrderStatusChip
                                size="small"
                                status={order.ordsercabest}
                                sx={{
                                  fontWeight: 'bold'
                                }}
                              />
                            </TableCell>

                            <TableCell>{formatDate(order.ordsercabfec)}</TableCell>

                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={`${valorizableManifests.length}/${manifests.length}`}
                                color={valorizableManifests.length > 0 ? 'success' : 'default'}
                                variant={valorizableManifests.length > 0 ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 'bold', minWidth: 56 }}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <Typography fontWeight="bold">
                                {formatCurrency(order.ordsercabpre, symbol)}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {isExpanded && (
                            <TableRow>
                              <TableCell
                                colSpan={12}
                                sx={{
                                  backgroundColor: '#F8FAFC',
                                  p: 0,
                                  borderBottom: '1px solid #E5E7EB',
                                }}
                              >
                                <Box p={2}>
                                  {manifests.length === 0 ? (
                                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                                      Esta OS no tiene manifiestos asociados.
                                    </Alert>
                                  ) : (
                                    <Paper
                                      variant="outlined"
                                      sx={{ borderRadius: 2.5, overflow: 'hidden' }}
                                    >
                                      <Box
                                        sx={{
                                          px: 2,
                                          py: 1,
                                          backgroundColor: '#fff',
                                          borderBottom: '1px solid #E5E7EB',
                                        }}
                                      >
                                        <Typography variant="body2" fontWeight="bold">
                                          Manifiestos asociados
                                        </Typography>
                                      </Box>

                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell><strong>Manifiesto</strong></TableCell>
                                            <TableCell><strong>Trayecto</strong></TableCell>
                                            <TableCell><strong>T. Vehiculo</strong></TableCell>
                                            <TableCell><strong>Fecha</strong></TableCell>
                                            <TableCell><strong>Origen</strong></TableCell>
                                            <TableCell><strong>Destino</strong></TableCell>
                                            <TableCell align="right"><strong>Precio</strong></TableCell>
                                            <TableCell><strong>Estado</strong></TableCell>
                                          </TableRow>
                                        </TableHead>

                                        <TableBody>
                                          {manifests.map((mn) => {
                                            const tramo = TRAMO_CONFIG[mn.ordsermantra];
                                            const manifestStatus =
                                              MANIFEST_STATUS_CONFIG[mn.manest] || {
                                                label: mn.manest,
                                                color: '#ccc',
                                                text: '#000',
                                              };

                                            return (
                                              <TableRow key={mn.manid} hover>
                                                <TableCell>
                                                  <Typography fontWeight="bold" color="primary">
                                                    MN-{String(mn.manid).padStart(6, '0')}
                                                  </Typography>
                                                </TableCell>

                                                <TableCell>
                                                  {tramo ? (
                                                    <Chip
                                                      size="small"
                                                      label={tramo.label}
                                                      variant="outlined"
                                                      sx={{
                                                        borderColor: tramo.color,
                                                        color: tramo.color,
                                                        fontWeight: 800,
                                                      }}
                                                    />
                                                  ) : (
                                                    mn.ordsermantra || '-'
                                                  )}
                                                </TableCell>
                                                <TableCell>{mn.vehiculo?.vehdes}</TableCell>

                                                <TableCell>{formatDate(mn.manfec)}</TableCell>

                                                <TableCell sx={{ maxWidth: 220 }}>
                                                  <Typography noWrap title={mn.manori}>
                                                    {mn.manori || '-'}
                                                  </Typography>
                                                </TableCell>

                                                <TableCell sx={{ maxWidth: 220 }}>
                                                  <Typography noWrap title={mn.mandes}>
                                                    {mn.mandes || '-'}
                                                  </Typography>
                                                </TableCell>

                                                <TableCell align="right">
                                                  <Typography fontWeight="bold">
                                                    {formatCurrency(mn.ordsermanpre, symbol)}
                                                  </Typography>
                                                </TableCell>

                                                <TableCell>
                                                  <Chip
                                                    size="small"
                                                    label={manifestStatus.label}
                                                    sx={{
                                                      bgcolor: manifestStatus.color,
                                                      color: manifestStatus.text,
                                                      fontWeight: 800,
                                                    }}
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </Paper>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={mergedOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage="Filas por página"
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#fff',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          width="100%"
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length > 0
              ? `${selectedIds.length} OS seleccionada(s), ${selectedManifestCount} manifiesto(s) valorizable(s).`
              : 'No hay órdenes seleccionadas.'}
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 'bold',
                px: 2.5,
              }}
            >
              {loading
                ? isEditMode
                  ? 'Guardando...'
                  : 'Creando...'
                : isEditMode
                  ? 'Guardar cambios'
                  : 'Crear ítem'}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default EligibleOrdersDialog;